import { useEffect, useMemo, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Loader2 } from "lucide-react";
import { formatCompactCurrency } from "@/lib/utils";

type MetricType = "clients" | "mrr" | "agencies";

type Product = { id: string; name: string };

type HistoryResponse = {
  products: Product[];
  series: Record<string, string | number>[];
};

const COLORS = [
  { stroke: "#6366f1", fill: "chartGrad0" },
  { stroke: "#f59e0b", fill: "chartGrad1" },
  { stroke: "#10b981", fill: "chartGrad2" },
];

const GRADIENT_DEFS = [
  { id: "chartGrad0", color: "#6366f1" },
  { id: "chartGrad1", color: "#f59e0b" },
  { id: "chartGrad2", color: "#10b981" },
];

const TITLES: Record<MetricType, string> = {
  clients: "Clients Over Time",
  mrr: "MRR Over Time",
  agencies: "Agencies Over Time",
};

export function SubscriptionChart({ metric }: { metric: MetricType }) {
  const [data, setData] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`/api/stripe/metrics/history?metric=${metric}`, {
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        return res.json() as Promise<HistoryResponse>;
      })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [metric]);

  const hasData = useMemo(
    () => data && data.series.length >= 2,
    [data]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-xl border bg-card py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border bg-card px-5 py-8 text-center text-sm text-muted-foreground">
        Failed to load chart data
      </div>
    );
  }

  if (!hasData || !data) {
    return (
      <div className="rounded-xl border bg-card px-5 py-8 text-center text-sm text-muted-foreground">
        Not enough data to display chart. Sync subscriptions to populate.
      </div>
    );
  }

  const isMrr = metric === "mrr";

  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{TITLES[metric]}</h3>
        <div className="flex items-center gap-4">
          {data.products.map((p, i) => (
            <div key={p.id} className="flex items-center gap-1.5">
              <div
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: COLORS[i % COLORS.length].stroke }}
              />
              <span className="text-xs text-muted-foreground">{p.name}</span>
            </div>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <AreaChart data={data.series}>
          <defs>
            {GRADIENT_DEFS.map((g) => (
              <linearGradient key={g.id} id={g.id} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={g.color} stopOpacity={0.15} />
                <stop offset="100%" stopColor={g.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#e2e8f0"
          />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11 }}
            tickFormatter={(v: string) => {
              const d = new Date(v + "T00:00:00");
              return d.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              });
            }}
            stroke="#94a3b8"
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            stroke="#94a3b8"
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            tickFormatter={isMrr ? (v: number) => formatCompactCurrency(v) : undefined}
          />
          <Tooltip
            contentStyle={{
              fontSize: 12,
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
            labelFormatter={(v) => {
              const d = new Date(String(v) + "T00:00:00");
              return d.toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              });
            }}
            formatter={(value, name) => {
              const numVal = typeof value === "number" ? value : 0;
              const product = data.products.find((p) => p.id === name);
              const label = product?.name ?? String(name);
              if (isMrr) return [formatCompactCurrency(numVal), label];
              return [numVal.toLocaleString(), label];
            }}
          />
          {data.products.map((p, i) => (
            <Area
              key={p.id}
              type="monotone"
              dataKey={p.id}
              name={p.id}
              stackId="1"
              stroke={COLORS[i % COLORS.length].stroke}
              strokeWidth={2}
              fill={`url(#${COLORS[i % COLORS.length].fill})`}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
